import { checkVerificationCode, resendVerificationCode } from "@/services/formDynamicApis";
import { Button, Modal, Stack, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import styles from "./VerificationCodeModal.module.scss";

const VERIFICATION_CODE_LENGTH = 10;
const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

export interface VerificationResendTexts {
  prompt: string;
  button: string;
  /** `{seconds}` is replaced with the remaining cooldown. */
  countdown: string;
  /** `{email}` is replaced with the masked address the backend reports. */
  sent: string;
  sentNoEmail: string;
  failed: string;
  /** The application has no email to send to (422). */
  noEmail?: string;
}

interface VerificationCodeModalProps {
  opened: boolean;
  onVerified: (token: string) => void;
  talentCode: string;
  primaryColor: string;
  title: string;
  description: string;
  buttonText: string;
  hint?: string;
  hostName?: string;
  /** Shows the "resend code" action when given. */
  resendTexts?: VerificationResendTexts;
  locale?: string;
}

export default function VerificationCodeModal({
  opened,
  onVerified,
  talentCode,
  primaryColor,
  title,
  description,
  buttonText,
  hint,
  hostName,
  resendTexts,
  locale,
}: VerificationCodeModalProps) {
  const [otpDigits, setOtpDigits] = useState(Array(VERIFICATION_CODE_LENGTH).fill(""));
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!resendTexts || resendLoading || resendCooldown > 0) return;
    setResendLoading(true);
    setResendNotice(null);
    const res = await resendVerificationCode(talentCode, hostName, locale);
    setResendLoading(false);
    const retryAfter = Number(res.data?.retryAfterSeconds);
    if (res.isError) {
      if (res.errorCode === "code-resend-1-429") {
        setResendCooldown(retryAfter > 0 ? retryAfter : DEFAULT_RESEND_COOLDOWN_SECONDS);
      }
      // Backend messages here are developer-facing ("Cannot POST ...").
      setOtpError(
        res.errorCode === "code-resend-1-no-email" && resendTexts.noEmail
          ? resendTexts.noEmail
          : resendTexts.failed
      );
      return;
    }
    setOtpError(null);
    setResendCooldown(retryAfter > 0 ? retryAfter : DEFAULT_RESEND_COOLDOWN_SECONDS);
    setResendNotice(
      res.data?.maskedEmail
        ? resendTexts.sent.replace("{email}", res.data.maskedEmail)
        : resendTexts.sentNoEmail
    );
  };

  const filledCount = otpDigits.filter(Boolean).length;
  const progressPct = Math.round((filledCount / VERIFICATION_CODE_LENGTH) * 100);

  const handleOtpConfirm = async (digits?: string[]) => {
    const code = (digits ?? otpDigits).join("").trim();
    if (code.length < VERIFICATION_CODE_LENGTH) return;
    setOtpLoading(true);
    setOtpError(null);
    const res = await checkVerificationCode(talentCode, code, hostName);
    setOtpLoading(false);
    if (res.isError) {
      setOtpError(res.message || "Mã xác thực không đúng. Vui lòng thử lại.");
      setOtpDigits(Array(VERIFICATION_CODE_LENGTH).fill(""));
      otpRefs.current[0]?.focus();
    } else {
      onVerified(res.data?.token ?? "");
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      withCloseButton={false}
      overlayOpacity={0.6}
      overlayBlur={8}
      centered
      size={560}
      radius={24}
      padding={0}
      styles={{
        modal: {
          width: "min(560px, calc(100vw - 32px))",
          maxWidth: "calc(100vw - 32px)",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(0,0,0,0.18), 0 8px 24px rgba(0,0,0,0.10)",
        },
        overlay: {
          backdropFilter: "blur(8px)",
        },
      }}
    >
      {/* Decorative gradient header strip */}
      {/* <div style={{
        height: 6,
        background: `linear-gradient(90deg, ${primaryColor} 0%, ${primaryColor}99 50%, ${primaryColor}33 100%)`,
        borderRadius: "24px 24px 0 0",
      }} /> */}

      <Stack spacing={0} className={styles.modalContent}>
        {/* Icon + Title */}
        <Stack spacing={8} align="center" mb={28} className={styles.modalHeader}>
          <div className={styles.iconWrap} style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}11 100%)`,
            border: `2px solid ${primaryColor}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 4,
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <polyline points="9 12 11 14 15 10"/>
            </svg>
          </div>

          <Text fw={700} fz={22} color="#0F172A" className={styles.title}>
            {title}
          </Text>
          <Text fz={14} color="#64748B" ta="center" className={styles.description}>
            {description}
          </Text>
        </Stack>

        {/* OTP input grid */}
        <div style={{ marginBottom: 24 }}>
          <div className={styles.otpGrid}>
            {otpDigits.map((digit, i) => (
              <input
                key={i}
                className={styles.otpInput}
                ref={(el) => { otpRefs.current[i] = el as HTMLInputElement | null; }}
                value={digit}
                disabled={otpLoading}
                inputMode="numeric"
                maxLength={1}
                autoComplete="one-time-code"
                style={{
                  color: digit ? "#0F172A" : "#94A3B8",
                  background: digit ? `${primaryColor}0d` : "#F8FAFC",
                  border: `2px solid ${digit ? primaryColor : "#E2E8F0"}`,
                  outline: "none",
                  cursor: otpLoading ? "not-allowed" : "text",
                  transition: "border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease",
                  boxShadow: digit ? `0 0 0 3px ${primaryColor}22` : "none",
                  transform: digit ? "scale(1.04)" : "scale(1)",
                  opacity: otpLoading ? 0.6 : 1,
                }}
                onChange={(e) => {
                  const val = e.currentTarget.value.replace(/\D/g, "").slice(-1);
                  const next = [...otpDigits];
                  next[i] = val;
                  setOtpDigits(next);
                  if (otpError) setOtpError(null);
                  if (val && i < VERIFICATION_CODE_LENGTH - 1) otpRefs.current[i + 1]?.focus();
                  if (val && i === VERIFICATION_CODE_LENGTH - 1) handleOtpConfirm(next);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !otpDigits[i] && i > 0) {
                    otpRefs.current[i - 1]?.focus();
                  }
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = primaryColor;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryColor}33`;
                }}
                onBlur={(e) => {
                  if (!e.currentTarget.value) {
                    e.currentTarget.style.borderColor = "#E2E8F0";
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, VERIFICATION_CODE_LENGTH);
                  if (pasted.length > 0) {
                    e.preventDefault();
                    const next = Array(VERIFICATION_CODE_LENGTH).fill("");
                    pasted.split("").forEach((ch, idx) => { next[idx] = ch; });
                    setOtpDigits(next);
                    const focusIdx = Math.min(pasted.length, VERIFICATION_CODE_LENGTH - 1);
                    otpRefs.current[focusIdx]?.focus();
                    if (pasted.length === VERIFICATION_CODE_LENGTH) handleOtpConfirm(next);
                  }
                }}
              />
            ))}
          </div>

          {/* Progress bar */}
          {filledCount > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{
                height: 3,
                background: "#E2E8F0",
                borderRadius: 999,
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%",
                  width: `${progressPct}%`,
                  background: filledCount === VERIFICATION_CODE_LENGTH
                    ? "#22C55E"
                    : `linear-gradient(90deg, ${primaryColor}, ${primaryColor}cc)`,
                  borderRadius: 999,
                  transition: "width 0.2s ease, background 0.3s ease",
                }} />
              </div>
              <Text fz={12} color="#94A3B8" mt={6} ta="right">
                {filledCount}/{VERIFICATION_CODE_LENGTH}
              </Text>
            </div>
          )}
        </div>

        {/* Inline error */}
        {otpError && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            marginBottom: 12,
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: 10,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <Text fz={13} color="#DC2626" style={{ lineHeight: 1.5 }}>
              {otpError}
            </Text>
          </div>
        )}

        {/* Submit button */}
        <Button
          fullWidth
          size="md"
          radius={12}
          loading={otpLoading}
          disabled={filledCount < VERIFICATION_CODE_LENGTH}
          onClick={() => handleOtpConfirm()}
          style={{
            height: 52,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "0.1px",
            background: filledCount === VERIFICATION_CODE_LENGTH ? primaryColor : "#E2E8F0",
            color: filledCount === VERIFICATION_CODE_LENGTH ? "#fff" : "#94A3B8",
            transition: "background 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease",
            boxShadow: filledCount === VERIFICATION_CODE_LENGTH ? `0 4px 16px ${primaryColor}44` : "none",
            cursor: filledCount === VERIFICATION_CODE_LENGTH && !otpLoading ? "pointer" : "not-allowed",
          }}
        >
          {buttonText}
        </Button>

        {/* Resend */}
        {resendTexts && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            {resendNotice && (
              <Text fz={13} color="#15803D" mb={6} style={{ lineHeight: 1.5 }}>
                {resendNotice}
              </Text>
            )}
            <Text fz={13} color="#64748B" component="span">
              {resendTexts.prompt}{" "}
            </Text>
            <Button
              variant="subtle"
              compact
              loading={resendLoading}
              disabled={resendCooldown > 0 || otpLoading}
              onClick={handleResend}
              style={{ color: resendCooldown > 0 ? "#94A3B8" : primaryColor, fontWeight: 600 }}
            >
              {resendCooldown > 0
                ? resendTexts.countdown.replace("{seconds}", String(resendCooldown))
                : resendTexts.button}
            </Button>
          </div>
        )}

        {/* Hint */}
        <div
          className={styles.hint}
          style={{
            // background: "#FFFBEB",
            // border: "1px solid #FDE68A",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <Text fz={12} color="#92400E" style={{ lineHeight: 1.6 }}>
            {hint || "Mã xác thực bảo mật — không chia sẻ với người khác."}
          </Text>
        </div>
      </Stack>

    </Modal>
  );
}
